import React from "react";
import { useTranslation } from "react-i18next";
import { ScanLine, Undo2, Filter, Store, MapPin, AlertTriangle } from "lucide-react";
import { Table, THead, TBody, TR, TH, TD } from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Button } from "../../../components/ui/Button";
import { OUTCOME_META } from "./outcome";
import { recipientName, comercioName, comunaName } from "./mockData";

const hhmm = (ts) => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function StatusBadge({ outcome }) {
  const { t } = useTranslation();
  const meta = OUTCOME_META[outcome] || OUTCOME_META.notfound;
  const Icon = meta.Icon;
  return (
    <Badge tone={meta.tone} className="gap-1">
      <Icon className="h-3.5 w-3.5" />
      {t(meta.label)}
    </Badge>
  );
}

export default function SessionList({ items, ordersById, onOpenDetail, onUndo, hasAny, filter, onResetFilter }) {
  const { t } = useTranslation();

  const wrap = "bg-bg border border-border rounded-xl shadow-card overflow-hidden";

  if (!hasAny) {
    return (
      <div className={wrap}>
        <EmptyState
          icon={<ScanLine />}
          title={t("Aún no recibes pedidos")}
          description={t("Cada pedido que escanees o registres aparecerá aquí, con su hora y estado.")}
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={wrap}>
        <EmptyState
          icon={<Filter />}
          title={t("Sin resultados para este filtro")}
          description={t("No hay registros que coincidan con el filtro seleccionado.")}
          action={<Button variant="outline" onClick={onResetFilter}>{t("Ver todos")}</Button>}
        />
      </div>
    );
  }

  return (
    <>
      {/* desktop */}
      <div className={`hidden md:block ${wrap}`}>
        <Table>
          <THead>
            <TR>
              <TH>{t("Hora")}</TH>
              <TH>{t("Código")}</TH>
              <TH>{t("Comercio")}</TH>
              <TH>{t("Destinatario")}</TH>
              <TH>{t("Comuna")}</TH>
              <TH>{t("Estado")}</TH>
              <TH className="text-right">{t("Acción")}</TH>
            </TR>
          </THead>
          <TBody>
            {items.map((it) => {
              const order = it.orderId ? ordersById[it.orderId] : null;
              const clickable = !!order;
              return (
                <TR
                  key={it.id}
                  className={clickable ? "cursor-pointer" : ""}
                  onClick={clickable ? () => onOpenDetail(order) : undefined}
                >
                  <TD className="text-fg-muted tabular-nums whitespace-nowrap">{hhmm(it.ts)}</TD>
                  <TD className="font-mono font-medium tabular-nums whitespace-nowrap">{it.code}</TD>
                  <TD className={it.mismatch ? "text-status-warning font-medium" : "text-fg-muted"}>
                    {order ? comercioName(order) : "—"}
                    {it.mismatch && (
                      <AlertTriangle className="inline-block ml-1 h-3.5 w-3.5 align-text-bottom" aria-label={t("Otro comercio")} />
                    )}
                  </TD>
                  <TD className="font-medium">{order ? recipientName(order) : "—"}</TD>
                  <TD className="text-fg-muted">{order ? comunaName(order) : "—"}</TD>
                  <TD><StatusBadge outcome={it.outcome} /></TD>
                  <TD className="text-right">
                    {it.outcome === "success" && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onUndo(it); }}
                        className="inline-flex min-h-[40px] items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        title={t("Deshacer")}
                      >
                        <Undo2 className="h-3.5 w-3.5" />{t("Deshacer")}
                      </button>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>

      {/* móvil */}
      <div className="md:hidden space-y-2">
        {items.map((it) => {
          const order = it.orderId ? ordersById[it.orderId] : null;
          return (
            <div
              key={it.id}
              className="bg-bg border border-border rounded-xl shadow-card p-4"
              onClick={order ? () => onOpenDetail(order) : undefined}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-base font-semibold text-fg tabular-nums">{it.code}</span>
                <StatusBadge outcome={it.outcome} />
              </div>
              {order ? (
                <div className="mt-2 space-y-1 text-sm">
                  <p className="font-medium text-fg">{recipientName(order)}</p>
                  <p className="flex flex-wrap items-center gap-x-3 text-fg-muted">
                    <span className={`inline-flex items-center gap-1 ${it.mismatch ? "text-status-warning font-medium" : ""}`}>
                      <Store className="h-3.5 w-3.5" />{comercioName(order)}
                      {it.mismatch && <AlertTriangle className="h-3.5 w-3.5" aria-label={t("Otro comercio")} />}
                    </span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{comunaName(order)}</span>
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-sm text-fg-muted">{t("Sin coincidencia en el sistema.")}</p>
              )}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-fg-subtle tabular-nums">{hhmm(it.ts)}</span>
                {it.outcome === "success" && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onUndo(it); }}
                    className="inline-flex min-h-[40px] items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-fg-muted hover:bg-bg-muted"
                  >
                    <Undo2 className="h-3.5 w-3.5" />{t("Deshacer")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
